import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Child } from 'src/app/classes/Child';
import { environment } from 'src/environments/environment';

const JSON_HEADERS = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

/** Same shape as backend `AddChildRequest`. */
export interface ChildWritePayload {
  firstname: string;
  lastname: string;
  age: number;
  allergy?: string | null;
}

/**
 * `adminByParentPath` — `/v1/admin/users/{parentId}/children` (requires a matching server route).
 * `stockChildrenResource` — `/v1/children` (stock alef-school-api; parent is the JWT user).
 */
export type ChildrenApiMode = 'adminByParentPath' | 'stockChildrenResource';

@Injectable({
  providedIn: 'root',
})
export class ChildAdminService {
  private readonly apiRoot = environment.backendApiUrl;

  constructor(private http: HttpClient) {}

  /** Value from `environment` (defaults to `stockChildrenResource`). */
  public get apiMode(): ChildrenApiMode {
    return environment.childrenApiMode ?? 'stockChildrenResource';
  }

  /** `true` when using `/v1/children` paths (no `parentId` in the URL). */
  public get usesStockChildrenResource(): boolean {
    return this.apiMode === 'stockChildrenResource';
  }

  public addChild(
    parentUserId: number,
    payload: ChildWritePayload
  ): Observable<Child> {
    return this.http.post<Child>(
      this.addUrl(parentUserId),
      payload,
      JSON_HEADERS
    );
  }

  public updateChild(
    parentUserId: number,
    childId: number,
    payload: ChildWritePayload
  ): Observable<Child> {
    return this.http.put<Child>(
      this.url(parentUserId, childId),
      payload,
      JSON_HEADERS
    );
  }

  public deleteChild(
    parentUserId: number,
    childId: number
  ): Observable<void> {
    return this.http.delete<void>(
      this.url(parentUserId, childId),
      JSON_HEADERS
    );
  }

  /**
   * Create child route:
   * - stock: `/v1/children`
   * - admin: `/v1/admin/users/{parentId}/children`
   */
  private addUrl(parentUserId: number): string {
    if (this.usesStockChildrenResource) {
      return `${this.apiRoot}/v1/children`;
    }
    return `${this.apiRoot}/v1/admin/users/${parentUserId}/children`;
  }

  /**
   * Update/delete route:
   * - stock: `/v1/children/{id}`
   * - admin: `/v1/admin/users/{parentId}/children/{id}`
   */
  private url(parentUserId: number, childId?: number): string {
    if (this.usesStockChildrenResource) {
      const path =
        childId != null
          ? `${this.apiRoot}/v1/children/${childId}`
          : `${this.apiRoot}/v1/children`;
      return path;
    }
    const base = `${this.apiRoot}/v1/admin/users/${parentUserId}/children`;
    return childId != null ? `${base}/${childId}` : base;
  }
}
